import { PermissionDeniedError, ResourceNotFoundError, UnknownApplicationError } from '../../lib/errors';
import { logger } from '../../lib/logger';
import { supabase } from '../../lib/supabase-unified';
import Constants from 'expo-constants';
import {
  Course,
  Lesson,
  LessonMaterial,
  LessonVideo,
  LessonVideoChapter,
  UserLesson,
  UserLessonVideoInteraction
} from '../../types';

export interface LessonPageData {
  course: Course;
  lesson: Lesson;
  videos: LessonVideo[]; // Videos with chapters nested inside (from RPC)
  chapters: LessonVideoChapter[]; // Flat array (may be empty if chapters are nested in videos)
  materials: LessonMaterial[];
  user_lesson: UserLesson | null;
  user_videos: UserLessonVideoInteraction[];
  is_bookmarked: boolean;
}

/**
 * Legacy RPC-based implementation (kept for reference only)
 *
 * get_lesson_page_data_from_slugs_new
 *
 * const data = await handleSupabaseSingle<any>(
 *   supabase.rpc('get_lesson_page_data_from_slugs_new', {
 *     p_lesson_slug: lesson_slug,
 *     p_course_slug: course_slug,
 *     p_user_id: userId || null
 *   }) as any
 * );
 *
 * if (!data) {
 *   logger.warn('RPC returned null unexpectedly', {
 *     lesson_slug,
 *     course_slug,
 *     userId
 *   });
 *   throw new ResourceNotFoundError('Lesson data not available');
 * }
 *
 * return {
 *   course: data.course,
 *   lesson: data.lesson,
 *   videos: data.videos || [],
 *   chapters: data.chapters || [],
 *   materials: data.materials || [],
 *   user_lesson: data.user_lesson,
 *   user_videos: data.user_videos || [],
 *   is_bookmarked: data.is_bookmarked || false
 * };
 */

/**
 * Fetches lesson page data via Edge Function (multi-video support).
 *
 * @throws {ResourceNotFoundError} When course or lesson is not found (404)
 * @throws Other errors for network/unexpected issues
 */
// export const fetchLessonPageData = async (
//   lesson_slug: string,
//   course_slug: string,
//   userId?: string
// ): Promise<LessonPageData> => {
//   const data = await handleSupabaseSingle<any>(
//     supabase.rpc('get_lesson_page_data_from_slugs', {
//       p_lesson_slug: lesson_slug,
//       p_course_slug: course_slug,
//       p_user_id: userId || null
//     }) as any
//   );

//   // Log for debugging - RPC now throws errors instead of returning null
//   if (!data) {
//     logger.warn('RPC returned null unexpectedly', {
//       lesson_slug,
//       course_slug,
//       userId
//     });
//     throw new ResourceNotFoundError('Lesson data not available');
//   }

//   return {
//     course: data.course,
//     lesson: data.lesson,
//     chapters: data.chapters || [],
//     materials: data.materials || [],
//     user_lesson: data.user_lesson || createDefaultUserLesson(userId, data.lesson.id),
//     is_bookmarked: data.is_bookmarked || false
//   };
// };

/**
 * Fetches lesson page data via Edge Function (multi-video support).
 *
 * @throws {ResourceNotFoundError} When course or lesson is not found (404)
 * @throws Other errors for network/unexpected issues
 */
export const fetchLessonPageData = async (
  lesson_slug: string,
  course_slug: string,
  userId?: string
): Promise<LessonPageData> => {
  try {
    // Get the current session for the auth token
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const supabaseUrl =
      process.env.EXPO_PUBLIC_SUPABASE_URL ??
      (Constants.expoConfig?.extra?.supabaseUrl as string | undefined);
    const supabaseAnonKey =
      process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ||
      process.env.EXPO_PUBLIC_SUPABASE_KEY ||
      (Constants.expoConfig?.extra?.supabaseKey as string | undefined) ||
      '';

    if (!supabaseUrl) {
      logger.error('Missing Supabase URL (env or app.config extra)');
      throw new UnknownApplicationError('Supabase URL is not configured');
    }

    // Use the new-schema edge function
    const edgeFunctionUrl = `${supabaseUrl}/functions/v1/get-lesson-new`;

    const requestLesson = (accessToken?: string) =>
      fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: accessToken ? `Bearer ${accessToken}` : '',
          apikey: supabaseAnonKey,
        },
        body: JSON.stringify({
          course_slug,
          lesson_slug,
          // Forward-compatible: RPC now accepts p_user_id; edge function may read this.
          user_id: userId ?? null,
        }),
      });

    let response = await requestLesson(session?.access_token);

    // The edge function rejects the JWT before doing any work, so a 401 with a
    // token the client believed valid (slow clock, missed post-wake refresh)
    // is retried once with a freshly refreshed token.
    if (response.status === 401 && session?.access_token) {
      let freshToken: string | undefined;
      try {
        const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError) freshToken = refreshed.session?.access_token;
      } catch {
        // fall through with the original 401
      }
      if (freshToken && freshToken !== session.access_token) {
        response = await requestLesson(freshToken);
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `Request failed with status ${response.status}`;

      if (response.status === 403) {
        throw new PermissionDeniedError(errorMessage);
      }
      if (response.status === 404) {
        throw new ResourceNotFoundError(errorMessage);
      }

      throw new UnknownApplicationError(errorMessage, String(response.status));
    }

    const data = await response.json();

    if (!data || !data.course || !data.lesson) {
      logger.warn('Edge function returned incomplete data', {
        lesson_slug,
        course_slug,
        userId,
        hasData: !!data,
        hasCourse: !!data?.course,
        hasLesson: !!data?.lesson,
      });
      throw new ResourceNotFoundError('Lesson data not available');
    }

    return {
      course: data.course,
      lesson: data.lesson,
      // Edge function for new schema should already return videos + chapters + user_videos
      videos: data.videos || [],
      chapters: data.chapters || [],
      materials: data.materials || [],
      user_lesson: data.user_lesson || null,
      user_videos: data.user_videos || [],
      is_bookmarked: data.is_bookmarked || false,
    };
  } catch (error) {
    if (
      error instanceof PermissionDeniedError ||
      error instanceof ResourceNotFoundError ||
      error instanceof UnknownApplicationError
    ) {
      throw error;
    }

    logger.error('Failed to fetch lesson page data via edge function', {
      lesson_slug,
      course_slug,
      userId,
      error,
    });

    throw new UnknownApplicationError(
      error instanceof Error ? error.message : 'Failed to load lesson data',
    );
  }
};